import { Box, Button, Link as MuiLink, Typography, useTheme } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import { tokens } from '../../theme';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import TrafficIcon from '@mui/icons-material/Traffic';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Header from '../../components/Header';
import StatBox from '../../components/StatBox';
import { useState, useEffect, useMemo, useCallback } from 'react';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PublicIcon from '@mui/icons-material/Public';

function ActiveTripsToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 1, py: 0.5 }}>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [activeTripsLoading, setActiveTripsLoading] = useState(true);
  const [activeTripsExporting, setActiveTripsExporting] = useState(false);
  const [stats, setStats] = useState({
    totalPassengers: 0,
    totalBookings: 0,
    totalGuides: 0,
    totalExcursions: 0,
    totalPackages: 0,
    totalDestinations: 0,
    recentBookings: [],
  });
  const [activeTrips, setActiveTrips] = useState([]);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchDashboardStats = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/dashboard/stats`, { headers: { token } });
        const data = await res.json();
        if (data.success && data.data) {
          setStats(data.data);
        }
      } catch (err) {
        // Optionally handle error
      }
      setLoading(false);
    };
    fetchDashboardStats();
  }, [token]);

  const loadActiveTrips = useCallback(async () => {
    setActiveTripsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/dashboard/active-trips`, {
        headers: { token },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setActiveTrips(data.data);
      } else {
        setActiveTrips([]);
      }
    } catch (e) {
      setActiveTrips([]);
    } finally {
      setActiveTripsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadActiveTrips();
  }, [loadActiveTrips]);

  const exportActiveTripsExcel = async () => {
    setActiveTripsExporting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/dashboard/active-trips/export`, {
        headers: { token },
      });
      if (!res.ok) {
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'active-trips-dashboard.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      /* ignore */
    } finally {
      setActiveTripsExporting(false);
    }
  };

  const convertDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  const activeTripColumns = useMemo(
    () => [
      { field: 'serial', headerName: '#', width: 64, align: 'center', headerAlign: 'center' },
      {
        field: 'destinationName',
        headerName: 'Destination',
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'departureDate',
        headerName: 'Departure date',
        width: 130,
        valueFormatter: (p) => {
          if (!p.value) return '-';
          const d = new Date(p.value);
          return Number.isNaN(d.getTime()) ? String(p.value) : d.toLocaleDateString('en-GB');
        },
      },
      {
        field: 'endDate',
        headerName: 'End date',
        width: 130,
        valueFormatter: (p) => {
          if (!p.value) return '-';
          const d = new Date(p.value);
          return Number.isNaN(d.getTime()) ? String(p.value) : d.toLocaleDateString('en-GB');
        },
      },
      {
        field: 'guideName',
        headerName: 'Guide',
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <MuiLink
            component={Link}
            to={`/reports/guide-performance?guideId=${params.row.guideId}`}
            underline="hover"
            sx={{ color: colors.greenAccent[400], fontWeight: 600 }}
          >
            {params.value || '-'}
          </MuiLink>
        ),
      },
      { field: 'busNumber', headerName: 'Bus number', width: 120 },
      {
        field: 'totalPassengers',
        headerName: 'Total passengers',
        width: 130,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
      },
      {
        field: 'paidTotalAmount',
        headerName: 'Paid total (€)',
        width: 130,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        valueFormatter: (p) => `€ ${p.value == null ? '0.00' : Number(p.value).toFixed(2)}`,
      },
      {
        field: 'settlementAmount',
        headerName: 'Settlement (€)',
        width: 130,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        valueFormatter: (p) => `€ ${p.value == null ? '0.00' : Number(p.value).toFixed(2)}`,
      },
      {
        field: 'reservedTotalAmount',
        headerName: 'Reserved total (€)',
        width: 145,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        valueFormatter: (p) => `€ ${p.value == null ? '0.00' : Number(p.value).toFixed(2)}`,
      },
      {
        field: 'avgPerPassengerReserved',
        headerName: 'Avg/passenger (reserved)',
        width: 180,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        valueFormatter: (p) => `€ ${p.value == null ? '0.00' : Number(p.value).toFixed(2)}`,
      },
      {
        field: 'averagePerPassenger',
        headerName: 'Avg/passenger (paid)',
        width: 160,
        type: 'number',
        headerAlign: 'right',
        align: 'right',
        valueFormatter: (p) => `€ ${p.value == null ? '0.00' : Number(p.value).toFixed(2)}`,
      },
    ],
    [colors.greenAccent]
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Header title="DASHBOARD" subtitle="Welcome back! Here's what's happening with your trips at a glance." />
      </Box>

      <Box mt={4} p={2} sx={{ background: colors.primary[500], borderRadius: 0, boxShadow: 'none', mb: 1 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          }}
          gap={2}
        >
          <StatBox
            title={loading ? '...' : stats.totalPassengers}
            subtitle="Total Passengers"
            icon={<PersonAddIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={loading ? '...' : stats.totalBookings}
            subtitle="Total Bookings"
            icon={<PointOfSaleIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={loading ? '...' : stats.totalGuides}
            subtitle="Total Guides"
            icon={<TrafficIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={loading ? '...' : stats.totalExcursions}
            subtitle="Total Excursions"
            icon={<MenuBookIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={loading ? '...' : stats.totalPackages}
            subtitle="Total Packages"
            icon={<CardGiftcardIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={loading ? '...' : stats.totalDestinations}
            subtitle="Total Destinations"
            icon={<PublicIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
        </Box>
      </Box>

      {/* Request.pdf section C — Active trips */}
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Typography variant="h3" fontWeight="bold" color={colors.grey[100]}>
            Active trips (ongoing)
          </Typography>
          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={activeTripsExporting || activeTripsLoading}
            onClick={exportActiveTripsExcel}
            sx={{
              backgroundColor: colors.greenAccent[600],
              '&:hover': { backgroundColor: colors.greenAccent[700] },
            }}
          >
            {activeTripsExporting ? 'Exporting…' : 'Export Excel'}
          </Button>
        </Box>
        <Typography variant="body2" color={colors.grey[300]} mb={1.5}>
          Trips where today falls between departure and return date. Sorted by departure date (newest first). Click a
          guide to open Guide performance analytics.
        </Typography>
        <Box
          sx={{
            height: 560,
            width: '100%',
            '& .MuiDataGrid-root': { border: 'none' },
            '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[700]}` },
            '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700] },
            '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
            '& .MuiDataGrid-footerContainer': { backgroundColor: colors.blueAccent[700] },
          }}
        >
          <DataGrid
            rows={activeTrips}
            columns={activeTripColumns}
            getRowId={(row) => row.id}
            components={{ Toolbar: ActiveTripsToolbar }}
            disableSelectionOnClick
            loading={activeTripsLoading}
            initialState={{ pagination: { pageSize: 25 } }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{ color: colors.grey[100] }}
          />
        </Box>
      </Box>

      <Box mt={6} backgroundColor={colors.primary[400]} borderRadius={3} p={3}>
        <Typography variant="h3" fontWeight="bold" color={colors.grey[100]} mb={2}>
          Recent Bookings
        </Typography>
        <Box component="table" width="100%" sx={{ borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ backgroundColor: colors.blueAccent[700] }}>
              <Box component="th" sx={{ color: colors.grey[100], fontWeight: 'bold', p: 1, textAlign: 'left' }}>
                Contract No
              </Box>
              <Box component="th" sx={{ color: colors.grey[100], fontWeight: 'bold', p: 1, textAlign: 'left' }}>
                Passenger
              </Box>
              <Box component="th" sx={{ color: colors.grey[100], fontWeight: 'bold', p: 1, textAlign: 'left' }}>
                Destination
              </Box>
              <Box component="th" sx={{ color: colors.grey[100], fontWeight: 'bold', p: 1, textAlign: 'left' }}>
                Booking Date
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {stats.recentBookings && stats.recentBookings.length > 0 ? (
              stats.recentBookings.map((booking, idx) => (
                <Box
                  component="tr"
                  key={booking.contractNo || idx}
                  sx={{ borderBottom: `1px solid ${colors.primary[600]}` }}
                >
                  <Box component="td" sx={{ color: colors.grey[100], p: 1 }}>
                    {booking.contractNo || '-'}
                  </Box>
                  <Box component="td" sx={{ color: colors.grey[100], p: 1 }}>
                    {booking.passengerName ? `${booking.passengerName} ${booking.passengerSurname || ''}` : '-'}
                  </Box>
                  <Box component="td" sx={{ color: colors.grey[100], p: 1 }}>
                    {booking.destination || '-'}
                  </Box>
                  <Box component="td" sx={{ color: colors.grey[100], p: 1 }}>
                    {convertDate(booking.bookingStartDate)}
                  </Box>
                </Box>
              ))
            ) : (
              <Box component="tr">
                <Box component="td" colSpan={4} sx={{ color: colors.grey[300], p: 2, textAlign: 'center' }}>
                  No recent bookings found.
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
